import { DownloadFormat } from 'api/data/entries';
import {
	ExportCodelistModal,
	ExportMainDatasetModal,
	ExportSeriesModal
} from 'components/Dataset/Modals';
import { Dropdown } from 'components/UI/Dropdown';
import { Icon } from 'components/UI/Icons';
import { ExportType } from 'consts';
import { Svgs } from 'environment';
import { useNavigation } from 'hooks/navigation';
import {
	useEntriesErrors,
	useExpandGroupsContext,
	useImportVariableSet,
	usePermissions,
	useProject,
	useProjectId,
	useProjectMetadataDefinition,
	useTransferEntriesOwnershipContext,
	useUsername
} from 'hooks/store';
import { toast } from 'react-toastify';
import { useExportProjectMetadataMutation } from 'features/project-metadata-export/useExportProjectMetadataMutation';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useMemo, useState } from 'react';
import { Container } from './EntriesOptions.style';
import { useIsCodelistEnabledForProject } from 'features/codelist/useIsCodeListEnabled';
import { LanguageType } from 'types';
import { useLanguage } from 'features/hooks/useLanguage';
import { ROUTE_MAP } from 'features/entry-form-v2/utils/routeMap';
import { useGetProjectQuery } from '../../../../../features/entry-form-v2/data/useGetProjectQuery';

export interface ExportOptions {
	type: ExportType;
	format: DownloadFormat;
}

type Props = {
	hasVariableSets: boolean;
};

export function EntriesOptions({ hasVariableSets }: Props) {
	const language = useLanguage();
	const translation = DICTIONARY[language];

	const [projectId] = useProjectId();
	const [{ data: project }] = useProject();
	const userId = useUsername();

	const getProjectQuery = useGetProjectQuery({ projectId: projectId! }, { enabled: !!projectId });

	const { collapseAllGroups, expandAllGroups, expanded } = useExpandGroupsContext();

	const isCodelistEnabledForProject = useIsCodelistEnabledForProject();

	const allowToggleGroupsFeature = Object.keys(expanded).length > 0;
	const {
		hasExportDataAccess,
		hasDatasetWriteAccess,
		hasVariablesWriteAccess,
		hasDatasetReadAllAccess
	} = usePermissions();

	const hasCodelistExportAccess =
		getProjectQuery.data?.project.userAccess.accessCodelistExport ?? false;

	const hasCodelistWriteAccess =
		getProjectQuery.data?.project.userAccess.accessCodelistWrite ?? false;

	const { auditLogV1, teamCoreCoreExportProjectMetadata } = useFlags();

	const { navigate, routes } = useNavigation();

	const [{ data: projectMetadataDefinition }] = useProjectMetadataDefinition();

	const exportProjectMetadataMutation = useExportProjectMetadataMutation({
		projectId: projectId ?? '',
		metadataDefinition: projectMetadataDefinition?.metadataDefinition ?? null
	});

	const isProjectOwner = project?.projectOwnerDetails?.userId === userId;
	const canExportDataSet = project?.projectActions?.canExportDataSet ?? false;
	const isExportDenied = !hasExportDataAccess || !canExportDataSet;
	const exportDisabledTooltip = isExportDenied ? translation.exportDisabledTooltip : undefined;

	const {
		actions: { setIsTransferOwnership }
	} = useTransferEntriesOwnershipContext();

	const canCollapseGroups = useMemo(() => {
		let canCollapse = false;
		Object.values(expanded).forEach(expanded => {
			if (expanded) {
				canCollapse = true;
			}
		});
		return canCollapse;
	}, [expanded]);

	const canExpandGroups = useMemo(() => {
		let canExpand = false;
		Object.values(expanded).forEach(expanded => {
			if (!expanded) {
				canExpand = true;
			}
		});
		return canExpand;
	}, [expanded]);

	const [{ errors: entriesErrors }] = useEntriesErrors();

	const [exportMainDatasetModalVisible, setExportMainDatasetModalVisible] = useState(false);
	const [exportSeriesModalVisible, setExportSeriesModalVisible] = useState(false);
	const [exportCodelistModalVisible, setExportCodelistModalVisible] = useState(false);

	function onImportDataset() {
		if (projectId) navigate(routes.projects.import(projectId));
	}

	function onExportMetadata() {
		exportProjectMetadataMutation.mutate(undefined, {
			onSuccess: () => toast.success(translation.exportMetadataSuccess),
			onError: error => {
				console.error('Failed to export project metadata', error);
				toast.error(translation.exportMetadataError);
			}
		});
	}

	const [, { setIsImportVariableSet }] = useImportVariableSet();

	return (
		<>
			<Container>
				<Dropdown
					toggleComponent={({ ref, open, toggle }) => (
						<Icon
							dataTestId="entries-options-toggle"
							variant={v => v.button}
							ref={ref}
							svg={Svgs.More}
							active={open}
							onClick={toggle}
						/>
					)}
					width={18}
					offset={{ top: 20, right: 0 }}
				>
					{allowToggleGroupsFeature && (
						<>
							<Dropdown.Item
								title={translation.collapseAllGroups}
								onClick={() => collapseAllGroups()}
								disabled={!canCollapseGroups}
							/>
							<Dropdown.Item
								title={translation.expandAllGroups}
								disabled={!canExpandGroups}
								onClick={() => expandAllGroups()}
							/>
						</>
					)}
					{hasDatasetWriteAccess && hasVariablesWriteAccess && (
						<Dropdown.Item
							title={translation.importDataset}
							onClick={onImportDataset}
						/>
					)}
					{hasDatasetWriteAccess && hasVariablesWriteAccess && (
						<Dropdown.Item
							title={translation.importVariableSet}
							onClick={() => {
								onImportDataset();
								setIsImportVariableSet(true);
							}}
						/>
					)}
					<Dropdown.Item
						title={translation.exportDataset}
						disabled={!projectId || isExportDenied}
						tooltip={exportDisabledTooltip}
						tooltipId="entries-options-export-dataset-tooltip"
						onClick={() => setExportMainDatasetModalVisible(true)}
					/>
					<Dropdown.Item
						title={translation.exportSeries}
						disabled={!projectId || !hasVariableSets || isExportDenied}
						tooltip={exportDisabledTooltip}
						tooltipId="entries-options-export-series-tooltip"
						onClick={() => setExportSeriesModalVisible(true)}
					/>

					{/* Owner-only: exports project config (design), not entry data, so it's an owner
					prerogative rather than a data-export permission — like the codelist export below.
					UI affordance only; the real boundary is what the underlying requests enforce. */}
					{teamCoreCoreExportProjectMetadata && (
						<Dropdown.Item
							title={translation.exportMetadata}
							disabled={
								!projectId ||
								!isProjectOwner ||
								exportProjectMetadataMutation.isLoading
							}
							tooltip={
								!isProjectOwner
									? translation.exportMetadataDisabledTooltip
									: undefined
							}
							tooltipId="entries-options-export-metadata-tooltip"
							onClick={onExportMetadata}
						/>
					)}

					{auditLogV1 && hasDatasetReadAllAccess && (
						<Dropdown.Item
							title={translation.history}
							onClick={() => {
								navigate(routes.projects.dataset.history(projectId!));
							}}
						/>
					)}

					<Dropdown.Item
						title={translation.transferOwnership}
						disabled={!!entriesErrors}
						onClick={() => {
							setIsTransferOwnership(true);
						}}
					/>

					{isCodelistEnabledForProject && (
						<Dropdown.Item
							title={translation.importCodelist}
							disabled={!hasCodelistWriteAccess}
							tooltip={
								!hasCodelistWriteAccess
									? translation.importCodelistDisabledTooltip
									: undefined
							}
							tooltipId="entries-options-import-codelist-tooltip"
							onClick={() => {
								navigate(
									ROUTE_MAP.projects.byId.dataset.codelist.import.createPath({
										projectId: projectId!
									})
								);
							}}
						/>
					)}
					{isCodelistEnabledForProject && (
						<Dropdown.Item
							title={translation.exportCodelist}
							disabled={!isProjectOwner || !hasCodelistExportAccess}
							tooltip={
								!isProjectOwner || !hasCodelistExportAccess
									? translation.exportCodelistDisabledTooltip
									: undefined
							}
							tooltipId="entries-options-export-codelist-tooltip"
							onClick={() => setExportCodelistModalVisible(true)}
						/>
					)}
				</Dropdown>
			</Container>

			{exportMainDatasetModalVisible && (
				<ExportMainDatasetModal onClose={() => setExportMainDatasetModalVisible(false)} />
			)}
			{exportSeriesModalVisible && (
				<ExportSeriesModal onClose={() => setExportSeriesModalVisible(false)} />
			)}
			{exportCodelistModalVisible && projectId && (
				<ExportCodelistModal
					projectId={projectId}
					onClose={() => setExportCodelistModalVisible(false)}
				/>
			)}
		</>
	);
}

const DICTIONARY = {
	[LanguageType.English]: {
		collapseAllGroups: 'Collapse all groups',
		expandAllGroups: 'Expand all groups',
		importDataset: 'Import dataset',
		importVariableSet: 'Import series',
		exportDataset: 'Export dataset',
		exportSeries: 'Export series',
		exportMetadata: 'Export metadata',
		exportMetadataDisabledTooltip: 'Only the project owner can export project metadata',
		exportMetadataSuccess: 'Project metadata exported',
		exportMetadataError: 'Could not export project metadata. Please try again.',
		history: 'History',
		transferOwnership: 'Transfer ownership',
		importCodelist: 'Import codelist',
		exportCodelist: 'Export codelist',
		importCodelistDisabledTooltip:
			'You do not have permission to import codelists in this project',
		exportCodelistDisabledTooltip:
			'You do not have permission to export codelists in this project',
		exportDisabledTooltip: 'You do not have permission to export data from this project'
	},
	[LanguageType.Norwegian]: {
		collapseAllGroups: 'Skjul alle grupper',
		expandAllGroups: 'Ekspander alle grupper',
		importDataset: 'Importer datasett',
		importVariableSet: 'Importer serier',
		exportDataset: 'Eksporter datasett',
		exportSeries: 'Eksporter serier',
		exportMetadata: 'Eksporter metadata',
		exportMetadataDisabledTooltip: 'Kun prosjekteieren kan eksportere prosjektmetadata',
		exportMetadataSuccess: 'Prosjektmetadata eksportert',
		exportMetadataError: 'Kunne ikke eksportere prosjektmetadata. Prøv igjen.',
		history: 'Historikk',
		transferOwnership: 'Overfør eierskap',
		importCodelist: 'Importer kodeliste',
		exportCodelist: 'Eksporter kodeliste',
		importCodelistDisabledTooltip:
			'Du har ikke tillatelse til å importere kodelister i dette prosjektet',
		exportCodelistDisabledTooltip:
			'Du har ikke tillatelse til å eksportere kodelister i dette prosjektet',
		exportDisabledTooltip: 'Du har ikke tillatelse til å eksportere data fra dette prosjektet'
	}
} as const;
